import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDrag } from 'react-dnd';
import cx from 'classnames';
import useOutsideClick from '../../../hook/useOutsideClick';
import { deleteTaskService, partialUpdateTaskService } from '../../../services/tasks';
import { isResponseOk } from '../../../utility/isResponseOk';
import { ReactComponent as TaskIcon } from '../../../assets/images/task-icon.svg';
import DeleteTaskModal from './modals/delete-task-modal';
import { DND_TYPE_TASK, getDropTargetFolderId } from '../constants';

const TaskListItem = ({ task, setManageTaskModalData, setTasks, hasWritePermissions, handleMove, handleMoveError }) => {
  const menuButtonWrapperRef = useRef(null);
  const menuRef = useRef(null);
  const navigate = useNavigate();

  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ left: 0, right: 'initial' });

  useOutsideClick(menuButtonWrapperRef, () => setIsDropdownOpen(false));

  const [, drag] = useDrag(
    () => ({
      type: DND_TYPE_TASK,
      item: { item: task },
      canDrag: hasWritePermissions,
      end: (draggedItem, monitor) => {
        const dropResult = monitor.getDropResult();

        if (!dropResult?.item) {
          return;
        }

        const targetFolderId = getDropTargetFolderId(dropResult.item);

        // The task already sits there - nothing to move.
        if (targetFolderId === (draggedItem.item.folder_id ?? null)) {
          return;
        }

        partialUpdateTaskService({ folder_id: targetFolderId }, draggedItem.item.id).then((response) => {
          if (isResponseOk(response)) {
            handleMove(targetFolderId);
          } else {
            handleMoveError(true);
          }
        });
      },
    }),
    [task, hasWritePermissions, handleMove, handleMoveError],
  );

  useEffect(() => {
    const menuButton = menuButtonWrapperRef.current;
    if (menuButton && isDropdownOpen && menuRef.current) {
      const menuButtonRight = menuButton.getBoundingClientRect().right;

      // 300 - menu width
      if (menuButtonRight + 300 > window.innerWidth) {
        setMenuPosition({ left: 'initial', right: 0 });
      } else {
        setMenuPosition({ left: 0, right: 'initial' });
      }
    }
  }, [isDropdownOpen]);

  const handleDeleteTask = async () => {
    try {
      const response = await deleteTaskService(task.id);
      if (isResponseOk(response)) {
        setTasks((prevState) => prevState.filter((item) => item.id !== task.id));
      }
    } catch (e) {
      console.log(e);
    }
  };

  const formsCount = task.forms_count ?? task.task_forms?.length ?? 0;

  return (
    <>
      <div ref={drag} className="library-item list-item cursor-pointer tasks-row" onClick={() => navigate(`/tasks/${task.id}/forms`)}>
        <div className="library-item-text tasks-row-main">
          <TaskIcon className="tasks-row-icon" />
          <span className="tasks-row-name">{task.name}</span>
          <span className="tasks-row-forms-count">
            {formsCount} {formsCount === 1 ? 'form' : 'forms'}
          </span>
        </div>

        {hasWritePermissions && (
          <div ref={menuButtonWrapperRef} className={cx('library-item-button', { 'library-item-button-open': isDropdownOpen })}>
            <button
              className="btn btn-outline btn-small library-item-menu-button"
              onClick={(e) => {
                e.stopPropagation();
                setIsDropdownOpen(!isDropdownOpen);
              }}
            >
              <img src="/images/more-vertical.svg" alt="" />
            </button>
            {isDropdownOpen && (
              <div className="library-item-menu" ref={menuRef} style={{ left: menuPosition.left, right: menuPosition.right }}>
                <button
                  className="btn btn-full btn-dark"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsDropdownOpen(false);
                    setManageTaskModalData(task);
                  }}
                >
                  <i className="icon-icon-edit mr-2" />
                  Edit
                </button>
                <button
                  className="btn btn-full btn-remove"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsDropdownOpen(false);
                    setIsOpenDeleteModal(true);
                  }}
                >
                  <i className="icon-remove mr-2" />
                  Delete
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* A sibling of the row, not a child: a click inside the modal would
          otherwise bubble up to the row and open the task. */}
      {isOpenDeleteModal && <DeleteTaskModal closeModal={() => setIsOpenDeleteModal(false)} handleConfirm={handleDeleteTask} />}
    </>
  );
};

export default TaskListItem;
